"use client";

import { type KeyboardEvent, useEffect, useRef, useState } from "react";

interface EditableTextProps {
	text: string;
	onTextUpdate: (text: string) => void;
	placeholder?: string;
}

// NOTE: (bwilytsch): Use a form instead of an keydown handler
export function EditableText({
	text,
	onTextUpdate,
	placeholder = "Subject",
}: EditableTextProps): React.ReactElement {
	const inputRef = useRef<HTMLInputElement>(null);
	const [value, setValue] = useState(text);
	const [editable, setEditable] = useState(false);

	// Update internal state when text prop changes
	useEffect(() => {
		setValue(text);
	}, [text]);

	const show = (): void => {
		setEditable(true);
	};

	const hide = (): void => {
		setEditable(false);
	};

	const submit = (): void => {
		if (value !== text) {
			onTextUpdate(value);
		}
		hide();
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
		switch (e.key) {
			case "Enter":
				e.preventDefault();
				submit();
				break;
			case "Escape":
				e.preventDefault();
				hide();
				break;
		}
	};

	useEffect(() => {
		if (editable) {
			inputRef.current?.focus();
		}
	}, [editable]);

	if (editable) {
		return (
			<input
				className="w-full overflow-hidden text-ellipsis whitespace-nowrap bg-transparent px-md text-md text-text-body-muted outline-none"
				onBlur={submit}
				onChange={(e) => {
					setValue(e.target.value);
				}}
				onKeyDown={handleKeyDown}
				ref={inputRef}
				type="text"
				value={value}
				placeholder={placeholder}
			/>
		);
	}

	return (
		<div
			className="w-full overflow-hidden text-ellipsis whitespace-nowrap text-md text-text-body-muted text-center"
			onClick={show}
		>
			{value || placeholder}
		</div>
	);
}
